import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, GripVertical, Loader2, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQManager() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/faq`);
      setFaqs(res.data);
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        order: faq.order || 0,
      });
    } else {
      setEditingFaq(null);
      setFormData({
        question: '',
        answer: '',
        order: faqs.length,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question || !formData.answer) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingFaq) {
        await axios.put(`${API}/faq/${editingFaq.id}`, formData);
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(`${API}/faq`, formData);
        toast.success('FAQ added successfully');
      }
      setDialogOpen(false);
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await axios.delete(`${API}/faq/${id}`);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">FAQ Manager</h1>
          <p className="text-[#66605B]">Manage frequently asked questions</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
          data-testid="add-faq-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* FAQ List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
        </div>
      ) : faqs.length > 0 ? (
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={faq.id}
              className="bg-white rounded-sm shadow-sm p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#1B4D3E] text-white rounded-full flex items-center justify-center flex-shrink-0 font-serif">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg text-[#1C1C1C] mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-[#66605B] text-sm whitespace-pre-wrap">
                    {faq.answer}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenDialog(faq)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(faq.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-sm p-12 text-center">
          <HelpCircle className="w-12 h-12 mx-auto mb-4 text-[#66605B] opacity-30" />
          <p className="text-[#66605B]">No FAQs added yet</p>
          <Button
            onClick={() => handleOpenDialog()}
            variant="outline"
            className="mt-4"
          >
            Add First FAQ
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter the question..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Enter the answer..."
                rows={5}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                min="0"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingFaq ? 'Update' : 'Add'} FAQ
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
